import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  ArrowsUpDownIcon,
  FireIcon,
  TagIcon,
  PhotoIcon,
  CheckIcon,
  DocumentPlusIcon,
  PauseCircleIcon,
  UserGroupIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const ExerciseLibrary = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [exerciseToDelete, setExerciseToDelete] = useState(null);
  const [exerciseForm, setExerciseForm] = useState({
    name: '',
    description: '',
    category: 'upper-body',
    difficulty: 'intermediate',
    imageUrl: '',
    videoUrl: '',
    instructions: [''],
    targetMuscles: [''],
    equipment: [''],
    duration: '30',
    caloriesBurned: '150',
    restTime: '60'
  });
  
  // Catégories d'exercices
  const exerciseCategories = [
    { id: 'upper-body', name: 'Haut du corps' },
    { id: 'lower-body', name: 'Bas du corps' },
    { id: 'core', name: 'Core & Abdominaux' },
    { id: 'full-body', name: 'Corps complet' },
    { id: 'cardio', name: 'Cardio' },
    { id: 'flexibility', name: 'Flexibilité & Mobilité' },
  ];

  // Niveaux de difficulté
  const difficultyLevels = [
    { id: 'beginner', name: 'Débutant' },
    { id: 'intermediate', name: 'Intermédiaire' },
    { id: 'advanced', name: 'Avancé' },
    { id: 'expert', name: 'Expert' },
  ];
  
  // Données fictives pour les exercices
  const mockExercises = [
    {
      id: 'ex-001',
      name: 'Développé couché',
      description: 'Exercice classique pour développer les pectoraux',
      category: 'upper-body',
      difficulty: 'intermediate',
      imageUrl: null,
      videoUrl: 'https://www.youtube.com/watch?v=rT7DgCr-3pg',
      instructions: [
        'Allongez-vous sur un banc plat',
        'Saisissez la barre avec une prise légèrement plus large que les épaules',
        'Descendez la barre jusqu\'à toucher légèrement la poitrine',
        'Poussez la barre vers le haut jusqu\'à extension complète des bras'
      ],
      targetMuscles: ['Pectoraux', 'Triceps', 'Deltoïdes antérieurs'],
      equipment: ['Banc de musculation', 'Barre', 'Disques de poids'],
      duration: '45',
      caloriesBurned: '220',
      restTime: '90'
    },
    {
      id: 'ex-002',
      name: 'Squat',
      description: 'Exercice fondamental pour les jambes et le renforcement global',
      category: 'lower-body',
      difficulty: 'intermediate',
      imageUrl: null,
      videoUrl: 'https://www.youtube.com/watch?v=ultWZbUMPL8',
      instructions: [
        'Tenez-vous debout, pieds écartés à la largeur des épaules',
        'Descendez comme si vous vous asseyiez sur une chaise',
        'Gardez les genoux alignés avec les orteils',
        'Descendez jusqu\'à ce que vos cuisses soient parallèles au sol',
        'Remontez en poussant à travers vos talons'
      ],
      targetMuscles: ['Quadriceps', 'Ischio-jambiers', 'Fessiers', 'Lombaires'],
      equipment: ['Rack à squat', 'Barre', 'Disques de poids'],
      duration: '45',
      caloriesBurned: '250',
      restTime: '120'
    },
    {
      id: 'ex-003',
      name: 'Tractions',
      description: 'Développe la force du dos et des bras',
      category: 'upper-body',
      difficulty: 'advanced',
      imageUrl: null,
      videoUrl: 'https://www.youtube.com/watch?v=eGo4IYlbE5g',
      instructions: [
        'Saisissez la barre avec une prise légèrement plus large que les épaules',
        'Pendez avec les bras tendus',
        'Tirez votre corps vers le haut jusqu\'à ce que votre menton dépasse la barre',
        'Descendez de manière contrôlée'
      ],
      targetMuscles: ['Dorsaux', 'Biceps', 'Avant-bras', 'Rhomboïdes'],
      equipment: ['Barre de traction'],
      duration: '30',
      caloriesBurned: '180',
      restTime: '90'
    },
    {
      id: 'ex-004',
      name: 'Planche',
      description: 'Renforce le core et améliore la stabilité',
      category: 'core',
      difficulty: 'beginner',
      imageUrl: null,
      videoUrl: 'https://www.youtube.com/watch?v=ASdvN_XEl_c',
      instructions: [
        'Positionnez-vous face contre terre',
        'Soulevez votre corps sur les avant-bras et les orteils',
        'Maintenez votre corps en ligne droite',
        'Contractez vos abdominaux pendant toute la durée'
      ],
      targetMuscles: ['Abdominaux', 'Obliques', 'Lombaires', 'Épaules'],
      equipment: ['Aucun'],
      duration: '60',
      caloriesBurned: '40',
      restTime: '30'
    },
    {
      id: 'ex-005',
      name: 'Burpees',
      description: 'Exercice complet de cardio et renforcement musculaire',
      category: 'full-body',
      difficulty: 'advanced',
      imageUrl: null,
      videoUrl: 'https://www.youtube.com/watch?v=TU8QYVW0gDU',
      instructions: [
        'Commencez debout',
        'Descendez en position accroupie et placez vos mains au sol',
        'Sautez en arrière pour atteindre la position de planche',
        'Faites une pompe (optionnel)',
        'Ramenez vos pieds vers vos mains',
        'Sautez vers le haut avec les bras tendus au-dessus de la tête'
      ],
      targetMuscles: ['Quadriceps', 'Pectoraux', 'Épaules', 'Abdominaux', 'Cardio'],
      equipment: ['Aucun'],
      duration: '45',
      caloriesBurned: '300',
      restTime: '60'
    },
    {
      id: 'ex-006',
      name: 'Yoga Flow',
      description: 'Séquence fluide de poses de yoga pour améliorer la flexibilité',
      category: 'flexibility',
      difficulty: 'intermediate',
      imageUrl: null,
      videoUrl: 'https://www.youtube.com/watch?v=9kOCY0KNByw',
      instructions: [
        'Commencez en position du chien tête en bas',
        'Passez en fente basse',
        'Enchaînez avec le guerrier I',
        'Passez au guerrier II',
        'Terminez par la posture du triangle'
      ],
      targetMuscles: ['Ischio-jambiers', 'Hanches', 'Dos', 'Épaules'],
      equipment: ['Tapis de yoga'],
      duration: '120',
      caloriesBurned: '180',
      restTime: '30'
    },
    {
      id: 'ex-007',
      name: 'HIIT Sprint',
      description: 'Entraînement par intervalles à haute intensité sur tapis de course',
      category: 'cardio',
      difficulty: 'expert',
      imageUrl: null,
      videoUrl: 'https://www.youtube.com/watch?v=nf6aSCflSjY',
      instructions: [
        '30 secondes de sprint à 90% de votre capacité maximale',
        '30 secondes de repos actif (marche ou jogging lent)',
        'Répétez 10-15 fois'
      ],
      targetMuscles: ['Cardio', 'Quadriceps', 'Ischio-jambiers', 'Mollets'],
      equipment: ['Tapis de course'],
      duration: '20',
      caloriesBurned: '400',
      restTime: '30'
    },
    {
      id: 'ex-008',
      name: 'Soulevé de terre',
      description: 'Développe la force du bas du dos, des jambes et des bras',
      category: 'full-body',
      difficulty: 'advanced',
      imageUrl: null,
      videoUrl: 'https://www.youtube.com/watch?v=op9kVnSso6Q',
      instructions: [
        'Tenez-vous devant une barre au sol',
        'Fléchissez les hanches et les genoux pour saisir la barre',
        'Gardez le dos droit et la poitrine haute',
        'Soulevez en poussant à travers vos talons',
        'Étendez complètement vos hanches à la fin du mouvement'
      ],
      targetMuscles: ['Lombaires', 'Ischio-jambiers', 'Fessiers', 'Trapèzes'],
      equipment: ['Barre', 'Disques de poids'],
      duration: '45',
      caloriesBurned: '280',
      restTime: '120'
    },
  ];

  // Fetch exercises (mock)
  const { data: exercisesData, isLoading: isLoadingExercises } = useQuery('exercises', async () => {
    // Dans un environnement réel, ce serait une requête API
    // const response = await axios.get('/exercises');
    // return response.data;
    return { exercises: mockExercises };
  });

  const exercises = exercisesData?.exercises || [];

  // Filtrer les exercices selon les critères de recherche et de filtrage
  const filteredExercises = exercises.filter(ex => {
    const matchesSearch = ex.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          ex.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || ex.category === filterCategory;
    const matchesDifficulty = filterDifficulty === 'all' || ex.difficulty === filterDifficulty;
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  // Reset form when closing
  useEffect(() => {
    if (!isFormOpen) {
      setExerciseForm({
        name: '',
        description: '',
        category: 'upper-body',
        difficulty: 'intermediate',
        imageUrl: '',
        videoUrl: '',
        instructions: [''],
        targetMuscles: [''],
        equipment: [''],
        duration: '30',
        caloriesBurned: '150',
        restTime: '60'
      });
      setEditingExercise(null);
    }
  }, [isFormOpen]);

  // Fill form when editing
  useEffect(() => {
    if (editingExercise) {
      setExerciseForm({
        ...editingExercise,
      });
      setIsFormOpen(true);
    }
  }, [editingExercise]);

  // Gestion des champs de formulaire de type tableau
  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...exerciseForm[field]];
    newArray[index] = value;
    setExerciseForm(prev => ({ ...prev, [field]: newArray }));
  };

  const addArrayField = (field) => {
    setExerciseForm(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeArrayField = (field, index) => {
    if (exerciseForm[field].length > 1) {
      const newArray = exerciseForm[field].filter((_, i) => i !== index);
      setExerciseForm(prev => ({ ...prev, [field]: newArray }));
    }
  };

  // Gestion du formulaire
  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Vérifier les champs obligatoires
    if (!exerciseForm.name || !exerciseForm.category) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    toast.success(
      editingExercise 
        ? `Exercice "${exerciseForm.name}" mis à jour avec succès!` 
        : `Exercice "${exerciseForm.name}" ajouté à la bibliothèque!`
    );
    
    setIsFormOpen(false);
  };

  const handleDeleteExercise = () => {
    toast.success(`Exercice "${exerciseToDelete.name}" supprimé avec succès!`);
    setIsDeleteModalOpen(false);
    setExerciseToDelete(null);
  };

  const confirmDelete = (exercise) => {
    setExerciseToDelete(exercise);
    setIsDeleteModalOpen(true);
  };

  // Obtenir la couleur d'arrière-plan selon le niveau de difficulté
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/20 text-green-400';
      case 'intermediate': return 'bg-blue-500/20 text-blue-400';
      case 'advanced': return 'bg-orange-500/20 text-orange-400';
      case 'expert': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  // Obtenir le nom du niveau de difficulté
  const getDifficultyName = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'Débutant';
      case 'intermediate': return 'Intermédiaire';
      case 'advanced': return 'Avancé';
      case 'expert': return 'Expert';
      default: return 'Inconnu';
    }
  };

  // Obtenir le nom de la catégorie
  const getCategoryName = (categoryId) => {
    return exerciseCategories.find(c => c.id === categoryId)?.name || 'Inconnu';
  };

  return (
    <>
      <div className="space-y-6">
        {/* En-tête et outils */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="relative w-full max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
            <input
              type="text"
              placeholder="Rechercher un exercice..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-10"
              >
                <option value="all">Toutes les catégories</option>
                {exerciseCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <FunnelIcon className="absolute right-3 top-2.5 h-5 w-5 text-white/50 pointer-events-none" />
            </div>
            
            <div className="relative w-full sm:w-auto">
              <select
                value={filterDifficulty}
                onChange={(e) => setFilterDifficulty(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-10"
              >
                <option value="all">Tous les niveaux</option>
                {difficultyLevels.map(level => (
                  <option key={level.id} value={level.id}>{level.name}</option>
                ))}
              </select>
              <ArrowsUpDownIcon className="absolute right-3 top-2.5 h-5 w-5 text-white/50 pointer-events-none" />
            </div>
            
            <button 
              onClick={() => setIsFormOpen(true)}
              className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl flex items-center gap-2 hover:from-orange-600 hover:to-red-600 transition-all shadow-lg"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Nouvel exercice</span>
            </button>
          </div>
        </div>
        
        {/* Liste des exercices */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {isLoadingExercises ? (
            <div className="col-span-3 flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            </div>
          ) : filteredExercises.length > 0 ? (
            filteredExercises.map((exercise) => (
              <motion.div
                key={exercise.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300 overflow-hidden h-full flex flex-col"
              >
                {/* Image d'exercice */}
                <div className="h-48 bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center relative overflow-hidden">
                  {exercise.imageUrl ? (
                    <img 
                      src={exercise.imageUrl} 
                      alt={exercise.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-white/30">
                      <FireIcon className="h-16 w-16" />
                      <span className="text-sm mt-2">Pas d'image</span>
                    </div>
                  )}
                  
                  <div className="absolute top-0 right-0 p-2 flex space-x-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(exercise.difficulty)}`}>
                      {getDifficultyName(exercise.difficulty)}
                    </span>
                  </div>
                </div>
                
                {/* Contenu */}
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-semibold text-white">{exercise.name}</h3>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => setEditingExercise(exercise)}
                        className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => confirmDelete(exercise)}
                        className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-white/60 text-sm mt-2 line-clamp-2">{exercise.description}</p>
                  
                  <div className="mt-3">
                    <span className="text-white/80 text-sm flex items-center gap-1 mb-1">
                      <TagIcon className="h-4 w-4 text-white/50" /> 
                      <span>{getCategoryName(exercise.category)}</span>
                    </span>
                    
                    <div className="mt-2 flex flex-wrap gap-1">
                      {exercise.targetMuscles.slice(0, 3).map((muscle, idx) => (
                        <span 
                          key={idx} 
                          className="px-2 py-0.5 bg-white/10 rounded-lg text-white/70 text-xs"
                        >
                          {muscle}
                        </span>
                      ))}
                      {exercise.targetMuscles.length > 3 && (
                        <span className="px-2 py-0.5 bg-white/10 rounded-lg text-white/70 text-xs">
                          +{exercise.targetMuscles.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-auto pt-4 flex justify-between text-xs text-white/50">
                    <span className="flex items-center gap-1">
                      <PauseCircleIcon className="h-4 w-4" /> {exercise.duration}s
                    </span>
                    <span className="flex items-center gap-1">
                      <FireIcon className="h-4 w-4" /> {exercise.caloriesBurned} kcal
                    </span>
                    <span className="flex items-center gap-1">
                      <UserGroupIcon className="h-4 w-4" /> Tous niveaux
                    </span>
                  </div>
                </div>
                
                {/* Footer with actions */}
                <div className="border-t border-white/10 p-3 bg-white/5">
                  <button className="w-full py-2 bg-gradient-to-r from-orange-500/80 to-red-500/80 text-white rounded-lg hover:from-orange-500 hover:to-red-500 transition-all text-sm flex items-center justify-center gap-2">
                    <EyeIcon className="h-4 w-4" />
                    Voir les détails
                  </button>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="col-span-3 flex flex-col items-center justify-center h-64 bg-white/5 rounded-2xl border border-white/10">
              <DocumentPlusIcon className="h-16 w-16 text-white/20 mb-4" />
              <p className="text-white/60">Aucun exercice trouvé</p>
              <button 
                onClick={() => {
                  setSearchTerm('');
                  setFilterCategory('all');
                  setFilterDifficulty('all');
                }}
                className="mt-4 px-4 py-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-all text-sm"
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Formulaire d'exercice (modal) */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingExercise ? 'Modifier l\'exercice' : 'Nouvel exercice'}
              </h2>
              <button
                onClick={() => setIsFormOpen(false)}
                className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informations de base */}
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Nom de l'exercice*
                  </label>
                  <input
                    type="text"
                    value={exerciseForm.name}
                    onChange={(e) => setExerciseForm({...exerciseForm, name: e.target.value})}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Nom de l'exercice"
                  />
                </div>
                
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Catégorie*
                  </label>
                  <select
                    value={exerciseForm.category}
                    onChange={(e) => setExerciseForm({...exerciseForm, category: e.target.value})}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {exerciseCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-white text-sm font-medium mb-2">
                    Description
                  </label>
                  <textarea
                    value={exerciseForm.description}
                    onChange={(e) => setExerciseForm({...exerciseForm, description: e.target.value})}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[100px]"
                    placeholder="Description de l'exercice"
                  ></textarea>
                </div>
                
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Niveau de difficulté
                  </label>
                  <select
                    value={exerciseForm.difficulty}
                    onChange={(e) => setExerciseForm({...exerciseForm, difficulty: e.target.value})}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {difficultyLevels.map(level => (
                      <option key={level.id} value={level.id}>{level.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Image (URL)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={exerciseForm.imageUrl}
                      onChange={(e) => setExerciseForm({...exerciseForm, imageUrl: e.target.value})}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 pl-10"
                      placeholder="https://example.com/image.jpg"
                    />
                    <PhotoIcon className="absolute left-3 top-2.5 h-5 w-5 text-white/50" />
                  </div>
                </div>
              </div>
              
              {/* Boutons d'action */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all flex items-center gap-2 shadow-lg"
                >
                  <CheckIcon className="h-5 w-5" />
                  {editingExercise ? 'Mettre à jour' : 'Ajouter l\'exercice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Confirmation de suppression */}
      {isDeleteModalOpen && exerciseToDelete && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Confirmer la suppression</h3>
            <p className="text-white/80">
              Êtes-vous sûr de vouloir supprimer l'exercice <span className="font-semibold text-white">{exerciseToDelete.name}</span> ?
            </p>
            <p className="text-white/60 text-sm mt-2">
              Cette action est irréversible.
            </p>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteExercise}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExerciseLibrary; 